export enum InvoiceStatus {
	OPEN = '1',
	PARTIALLY_CLOSED = '2',
	CLOSED = '3',
	CANCELLED = '4',
}

export enum InvoiceType {
	INVOICE = '1',
	CREDIT_MEMO = '2',
}

export type InvoiceStatusKey = keyof typeof InvoiceStatus;
export type InvoiceStatusValue = (typeof InvoiceStatus)[InvoiceStatusKey];

export type InvoiceTypeKey = keyof typeof InvoiceType;
export type InvoiceTypeValue = (typeof InvoiceType)[InvoiceTypeKey];

/**
 * Human-readable labels for invoice statuses
 */
export const InvoiceStatusLabels: Record<string, string> = {
	[InvoiceStatus.OPEN]: 'Open',
	[InvoiceStatus.PARTIALLY_CLOSED]: 'Partially Closed',
	[InvoiceStatus.CLOSED]: 'Closed',
	[InvoiceStatus.CANCELLED]: 'Cancelled',
};

/**
 * Human-readable labels for invoice types
 */
export const InvoiceTypeLabels: Record<string, string> = {
	[InvoiceType.INVOICE]: 'Invoice',
	[InvoiceType.CREDIT_MEMO]: 'Credit Memo',
};

/**
 * Get human-readable status label from status code.
 *
 * D058 (UPDATE) fix (2026-05-26 E2E pass): empty-string status values
 * (which exist on legacy/placeholder rows in some tenants) used to
 * collapse to literal "Unknown". They now render as `-`, same as
 * null/undefined. The `?? 'Unknown'` fallback still applies for
 * genuinely unmapped non-empty status codes — those should never
 * appear in clean data but the fallback prevents a crash.
 */
export function getInvoiceStatusLabel(
	status: string | number | null | undefined,
): string {
	if (status == null) {
		return '-';
	}

	const statusCode = String(status).trim();

	if (statusCode === '') {
		return '-';
	}

	return InvoiceStatusLabels[statusCode] ?? 'Unknown';
}

/**
 * Get status color based on status code
 */
export function getInvoiceStatusColor(
	status: string | number | null | undefined,
): string {
	if (status == null) {
		return 'default';
	}

	const statusCode = String(status);

	switch (statusCode) {
		case InvoiceStatus.OPEN:
			return 'primary';
		case InvoiceStatus.PARTIALLY_CLOSED:
			return 'warning';
		case InvoiceStatus.CLOSED:
			return 'success';
		case InvoiceStatus.CANCELLED:
			return 'danger';
		default:
			return 'default';
	}
}

/**
 * Get human-readable type label from type code
 */
export function getInvoiceTypeLabel(
	type: string | number | null | undefined,
): string {
	if (type == null) {
		return 'Invoice';
	}

	const typeCode = String(type);

	return InvoiceTypeLabels[typeCode] ?? 'Invoice';
}

/**
 * Get type color based on type code
 */
export function getInvoiceTypeColor(
	type: string | number | null | undefined,
): string {
	if (type == null) {
		return 'primary';
	}

	const typeCode = String(type);

	switch (typeCode) {
		case InvoiceType.INVOICE:
			return 'primary';
		case InvoiceType.CREDIT_MEMO:
			return 'warning';
		default:
			return 'secondary';
	}
}

/**
 * Checks if an invoice status is considered active (not closed or cancelled)
 */
export function isInvoiceActive(status?: string | null): boolean {
	if (status == null) {
		return false;
	}

	return (
		status === InvoiceStatus.OPEN || status === InvoiceStatus.PARTIALLY_CLOSED
	);
}

/**
 * Checks if an invoice type is a credit memo
 */
export function isInvoiceCreditMemo(type?: string | null): boolean {
	return type === InvoiceType.CREDIT_MEMO;
}
